'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useRef } from 'react';

import type { AndroidMessage, Listener, MessageValues } from './types';
import { useNativeData } from './use-native-data';

/* istanbul ignore next */
const NativeDataContext = createContext<{
  postMessage: (value: MessageValues) => void;
  on: (type: string, handler: Listener) => void;
  off: (type: string, handler: Listener) => void;
  enabled: boolean;
}>({
  postMessage: () => null,
  on: () => null,
  off: () => null,
  enabled: false,
});

export const useNativeDataContext = () => useContext(NativeDataContext);

export const NativeDataProvider = ({ children }: { children: ReactNode }) => {
  const listeners = useRef<Record<string, Set<Listener>>>({});

  const on = useCallback((type: string, handler: Listener) => {
    /* istanbul ignore else */
    if (!listeners.current[type]) {
      listeners.current[type] = new Set();
    }
    listeners.current[type].add(handler);
  }, []);

  const off = useCallback((type: string, handler: Listener) => {
    listeners.current[type]?.delete(handler);
  }, []);

  const onMessage = useCallback((data: AndroidMessage) => {
    listeners.current[data.key]?.forEach((fn) => fn(data.value));
  }, []);

  const { postMessage, enabled } = useNativeData(onMessage);

  return (
    <NativeDataContext.Provider value={{ postMessage, on, off, enabled }}>
      {children}
    </NativeDataContext.Provider>
  );
};
